"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
Object.defineProperty(exports, "__esModule", { value: true });
var adjustBrowserBehavior_1 = require("./adjustBrowserBehavior");
var createEditorCore_1 = require("./createEditorCore");
var mapPluginEvents_1 = require("./mapPluginEvents");
var normalizeContentColor_1 = require("../darkMode/normalizeContentColor");
var calculateDefaultFormat_1 = require("../coreAPI/calculateDefaultFormat");
var convertContentToDarkMode_1 = require("../darkMode/convertContentToDarkMode");
var roosterjs_editor_dom_1 = require("roosterjs-editor-dom");
/**
 * RoosterJs core editor class
 */
var Editor = /** @class */ (function () {
    //#region Lifecycle
    /**
     * Creates an instance of Editor
     * @param contentDiv The DIV HTML element which will be the container element of editor
     * @param options An optional options object to customize the editor
     */
    function Editor(contentDiv, options) {
        var _this = this;
        if (options === void 0) { options = {}; }
        // 1. Make sure all parameters are valid
        if (roosterjs_editor_dom_1.getTagOfNode(contentDiv) != 'DIV') {
            throw new Error('contentDiv must be an HTML DIV element');
        }
        // 2. Store options values to local variables
        this.core = createEditorCore_1.default(contentDiv, options);
        this.enableExperimentFeatures = options.enableExperimentFeatures;
        // 3. Initialize plugins
        this.core.plugins.forEach(function (plugin) { return plugin.initialize(_this); });
        // 4. Ensure initial content and its format
        this.setContent(options.initialContent || contentDiv.innerHTML || '', false /*triggerContentChangedEvent*/);
        // 5. Create event handler to bind DOM events
        this.eventDisposers = mapPluginEvents_1.default(this.core);
        // 6. Add additional content edit features to the editor if specified
        if (options.additionalEditFeatures) {
            options.additionalEditFeatures.forEach(function (feature) { return _this.addContentEditFeature(feature); });
        }
        // 7. Make the container editable and set its selection styles
        if (!options.omitContentEditableAttributeChanges && !contentDiv.isContentEditable) {
            contentDiv.setAttribute('contenteditable', 'true');
            var styles = contentDiv.style;
            styles.userSelect = styles.msUserSelect = styles.webkitUserSelect = 'text';
            this.contenteditableChanged = true;
        }
        // 8. Do proper change for browsers to disable some browser-specified behaviors.
        adjustBrowserBehavior_1.default(this.core.document);
        // 9. Let plugins know that we are ready
        this.triggerPluginEvent(10 /* EditorReady */, {}, true /*broadcast*/);
        // 10. Before give editor to user, make sure there is at least one DIV element to accept typing
        this.core.corePlugins.typeInContainer.ensureTypeInElement(this.getFocusedPosition() || new roosterjs_editor_dom_1.Position(contentDiv, 0 /* Begin */));
    }
    /**
     * Dispose this editor, dispose all plugins and custom data
     */
    Editor.prototype.dispose = function () {
        this.triggerPluginEvent(11 /* BeforeDispose */, {}, true /*broadcast*/);
        this.core.plugins.forEach(function (plugin) { return plugin.dispose(); });
        this.eventDisposers.forEach(function (disposer) { return disposer(); });
        this.eventDisposers = null;
        for (var _i = 0, _a = Object.keys(this.core.customData); _i < _a.length; _i++) {
            var key = _a[_i];
            var data = this.core.customData[key];
            if (data && data.disposer) {
                data.disposer(data.value);
            }
            delete this.core.customData[key];
        }
        if (this.contenteditableChanged) {
            var styles = this.core.contentDiv.style;
            styles.userSelect = styles.msUserSelect = styles.webkitUserSelect = '';
            this.core.contentDiv.removeAttribute('contenteditable');
        }
        this.core = null;
    };
    /**
     * Get whether this editor is disposed
     * @returns True if editor is disposed, otherwise false
     */
    Editor.prototype.isDisposed = function () {
        return !this.core;
    };
    //#endregion
    //#region Node API
    /**
     * Insert node into editor
     * @param node The node to insert
     * @param option Insert options. Default value is:
     *  position: ContentPosition.SelectionStart
     *  updateCursor: true
     *  replaceSelection: true
     *  insertOnNewLine: false
     * @returns true if node is inserted. Otherwise false
     */
    Editor.prototype.insertNode = function (node, option) {
        // DocumentFragment type nodes become empty after they're inserted.
        // Therefore, we get the list of nodes to transform prior to their insertion.
        var darkModeOptions = this.getDarkModeOptions();
        var darkModeTransform = this.isDarkMode()
            ? convertContentToDarkMode_1.convertContentToDarkMode(node, darkModeOptions && darkModeOptions.onExternalContentTransform
                ? darkModeOptions.onExternalContentTransform
                : undefined)
            : null;
        var result = node ? this.core.api.insertNode(this.core, node, option) : false;
        if (result && darkModeTransform) {
            darkModeTransform();
        }
        return result;
    };
    /**
     * Delete a node from editor content
     * @param node The node to delete
     * @returns true if node is deleted. Otherwise false
     */
    Editor.prototype.deleteNode = function (node) {
        // Only remove the node when it falls within editor
        if (node && this.contains(node)) {
            node.parentNode.removeChild(node);
            return true;
        }
        return false;
    };
    /**
     * Replace a node in editor content with another node
     * @param existingNode The existing node to be replaced
     * @param new node to replace to
     * @param transformColorForDarkMode (optional) Whether to transform new node to dark mode. Default is false
     * @returns true if node is replaced. Otherwise false
     */
    Editor.prototype.replaceNode = function (existingNode, toNode, transformColorForDarkMode) {
        // Transform the new node to replace to
        var darkModeTransform = null;
        if (transformColorForDarkMode) {
            var darkModeOptions = this.getDarkModeOptions();
            darkModeTransform = this.isDarkMode()
                ? convertContentToDarkMode_1.convertContentToDarkMode(toNode, darkModeOptions && darkModeOptions.onExternalContentTransform
                    ? darkModeOptions.onExternalContentTransform
                    : undefined)
                : null;
        }
        // Only replace the node when it falls within editor
        if (existingNode && toNode && this.contains(existingNode)) {
            existingNode.parentNode.replaceChild(toNode, existingNode);
            if (darkModeTransform) {
                darkModeTransform();
            }
            return true;
        }
        return false;
    };
    /**
     * Get InlineElement at given node
     * @param node The node to create InlineElement
     * @returns The InlineElement result
     */
    Editor.prototype.getInlineElementAtNode = function (node) {
        return roosterjs_editor_dom_1.getInlineElementAtNode(this.core.contentDiv, node);
    };
    /**
     * Get BlockElement at given node
     * @param node The node to create InlineElement
     * @returns The BlockElement result
     */
    Editor.prototype.getBlockElementAtNode = function (node) {
        return roosterjs_editor_dom_1.getBlockElementAtNode(this.core.contentDiv, node);
    };
    Editor.prototype.contains = function (arg) {
        return roosterjs_editor_dom_1.contains(this.core.contentDiv, arg);
    };
    Editor.prototype.queryElements = function (selector, scopeOrCallback, callback) {
        if (scopeOrCallback === void 0) { scopeOrCallback = 0 /* Body */; }
        var scope = scopeOrCallback instanceof Function ? 0 /* Body */ : scopeOrCallback;
        callback = scopeOrCallback instanceof Function ? scopeOrCallback : callback;
        var range = scope == 0 /* Body */ ? null : this.getSelectionRange();
        return roosterjs_editor_dom_1.queryElements(this.core.contentDiv, selector, callback, scope, range);
    };
    /**
     * Collapse nodes within the given start and end nodes to their common ascenstor node,
     * split parent nodes if necessary
     * @param start The start node
     * @param end The end node
     * @param canSplitParent True to allow split parent node there are nodes before start or after end under the same parent
     * and the returned nodes will be all nodes from start trhough end after splitting
     * False to disallow split parent
     * @returns When cansplitParent is true, returns all node from start through end after splitting,
     * otherwise just return start and end
     */
    Editor.prototype.collapseNodes = function (start, end, canSplitParent) {
        return roosterjs_editor_dom_1.collapseNodes(this.core.contentDiv, start, end, canSplitParent);
    };
    //#endregion
    //#region Content API
    /**
     * Check whether the editor contains any visible content
     * @param trim Whether trime the content string before check. Default is false
     * @returns True if there's no visible content, otherwise false
     */
    Editor.prototype.isEmpty = function (trim) {
        return roosterjs_editor_dom_1.isNodeEmpty(this.core.contentDiv, trim);
    };
    /**
     * Get current editor content as HTML string
     * @param triggerExtractContentEvent Whether trigger ExtractContent event to all plugins
     * before return. Use this parameter to remove any temporary content added by plugins.
     * @param includeSelectionMarker Set to true if need include selection marker inside the content.
     * When restore this content, editor will set the selection to the position marked by these markers.
     * This parameter will be ignored when triggerExtractContentEvent is set to true
     * @returns HTML string representing current editor content
     */
    Editor.prototype.getContent = function (triggerExtractContentEvent, includeSelectionMarker) {
        if (triggerExtractContentEvent === void 0) { triggerExtractContentEvent = true; }
        if (includeSelectionMarker === void 0) { includeSelectionMarker = false; }
        var content = '';
        var isDarkMode = this.core.inDarkMode;
        if (triggerExtractContentEvent || isDarkMode) {
            var clonedRoot = this.core.contentDiv.cloneNode(true /*deep*/);
            var path = includeSelectionMarker && this.getSelectionPath();
            var range = path && roosterjs_editor_dom_1.createRange(clonedRoot, path.start, path.end);
            if (isDarkMode) {
                normalizeContentColor_1.default(clonedRoot);
            }
            if (triggerExtractContentEvent) {
                this.triggerPluginEvent(8 /* ExtractContentWithDom */, {
                    clonedRoot: clonedRoot,
                }, true /*broadcast*/);
                // TODO: Deprecated ExtractContentEvent once we have entity API ready in next major release
                content = this.triggerPluginEvent(7 /* ExtractContent */, { content: clonedRoot.innerHTML }, true /*broadcast*/).content;
            }
            else if (range) {
                // range is not null, which means we want to include a selection path in the content
                content = roosterjs_editor_dom_1.getHtmlWithSelectionPath(clonedRoot, range);
            }
            else {
                content = clonedRoot.innerHTML;
            }
        }
        else {
            content = roosterjs_editor_dom_1.getHtmlWithSelectionPath(this.core.contentDiv, includeSelectionMarker && this.getSelectionRange());
        }
        return content;
    };
    /**
     * Get plain text content inside editor
     * @returns The text content inside editor
     */
    Editor.prototype.getTextContent = function () {
        return roosterjs_editor_dom_1.getTextContent(this.core.contentDiv);
    };
    /**
     * Set HTML content to this editor. All existing content will be replaced. A ContentChanged event will be triggered
     * @param content HTML content to set in
     * @param triggerContentChangedEvent True to trigger a ContentChanged event. Default value is true
     */
    Editor.prototype.setContent = function (content, triggerContentChangedEvent) {
        if (triggerContentChangedEvent === void 0) { triggerContentChangedEvent = true; }
        var contentDiv = this.core.contentDiv;
        var contentChanged = false;
        if (contentDiv.innerHTML != content) {
            var range = roosterjs_editor_dom_1.setHtmlWithSelectionPath(contentDiv, content);
            this.select(range);
            contentChanged = true;
        }
        // Convert content even if it hasn't changed.
        if (this.core.inDarkMode) {
            var darkModeOptions = this.getDarkModeOptions();
            var convertFunction = convertContentToDarkMode_1.convertContentToDarkMode(contentDiv, darkModeOptions && darkModeOptions.onExternalContentTransform
                ? darkModeOptions.onExternalContentTransform
                : undefined, true /* skipRootElement */);
            if (convertFunction) {
                convertFunction();
                contentChanged = true;
            }
        }
        if (triggerContentChangedEvent && contentChanged) {
            this.triggerContentChangedEvent();
        }
    };
    /**
     * Insert HTML content into editor
     * @param HTML content to insert
     * @param option Insert options. Default value is:
     *  position: ContentPosition.SelectionStart
     *  updateCursor: true
     *  replaceSelection: true
     *  insertOnNewLine: false
     */
    Editor.prototype.insertContent = function (content, option) {
        if (content) {
            var allNodes = roosterjs_editor_dom_1.fromHtml(content, this.core.document);
            // If it is to insert on new line, and there are more than one node in the collection, wrap all nodes with
            // a parent DIV before calling insertNode on each top level sub node. Otherwise, every sub node may get wrapped
            // separately to show up on its own line
            if (option && option.insertOnNewLine && allNodes.length > 1) {
                allNodes = [roosterjs_editor_dom_1.wrap(allNodes)];
            }
            var fragment_1 = this.core.document.createDocumentFragment();
            allNodes.forEach(function (node) { return fragment_1.appendChild(node); });
            this.insertNode(fragment_1, option);
        }
    };
    /**
     * Paste into editor using a clipboardData object
     * @param clipboardData Clipboard data retrieved from clipboard
     * @param pasteAsText Force pasting as plain text. Default value is false
     * @param applyCurrentStyle True if apply format of current selection to the pasted content,
     * false to keep original foramt.  Default value is false. When pasteAsText is true, this parameter is ignored
     */
    Editor.prototype.paste = function (clipboardData, pasteAsText, applyCurrentFormat) {
        var _this = this;
        var range = this.getSelectionRange();
        var pos = range && roosterjs_editor_dom_1.Position.getStart(range);
        if (clipboardData && pos) {
            if (clipboardData.snapshotBeforePaste) {
                // Restore original content before paste a new one
                this.setContent(clipboardData.snapshotBeforePaste);
            }
            else {
                clipboardData.snapshotBeforePaste = this.getContent(false /*triggerExtractContentEvent*/, true /*includeSelectionMarker*/);
            }
            var fragment_2 = this.core.api.createPasteFragment(this.core, clipboardData, pos, pasteAsText, applyCurrentFormat);
            this.addUndoSnapshot(function () {
                _this.insertNode(fragment_2);
                return clipboardData;
            }, "Paste" /* Paste */);
        }
    };
    //#endregion
    //#region Focus and Selection
    /**
     * Get current selection range from Editor.
     * It does a live pull on the selection, if nothing retrieved, return whatever we have in cache.
     * @returns current selection range, or null if editor never got focus before
     */
    Editor.prototype.getSelectionRange = function () {
        return this.core.api.getSelectionRange(this.core, true /*tryGetFromCache*/);
    };
    /**
     * Get current selection in a serializable format
     * It does a live pull on the selection, if nothing retrieved, return whatever we have in cache.
     * @returns current selection path, or null if editor never got focus before
     */
    Editor.prototype.getSelectionPath = function () {
        var range = this.getSelectionRange();
        return range && roosterjs_editor_dom_1.getSelectionPath(this.core.contentDiv, range);
    };
    /**
     * Check if focus is in editor now
     * @returns true if focus is in editor, otherwise false
     */
    Editor.prototype.hasFocus = function () {
        return this.core.api.hasFocus(this.core);
    };
    /**
     * Focus to this editor, the selection was restored to where it was before, no unexpected scroll.
     */
    Editor.prototype.focus = function () {
        this.core.api.focus(this.core);
    };
    Editor.prototype.select = function (arg1, arg2, arg3, arg4) {
        var range = !arg1
            ? null
            : roosterjs_editor_dom_1.isRange(arg1)
                ? arg1
                : Array.isArray(arg1.start) && Array.isArray(arg1.end)
                    ? roosterjs_editor_dom_1.createRange(this.core.contentDiv, arg1.start, arg1.end)
                    : roosterjs_editor_dom_1.createRange(arg1, arg2, arg3, arg4);
        return this.contains(range) && this.core.api.selectRange(this.core, range);
    };
    /**
     * Get current selection
     * @return current selection object
     */
    Editor.prototype.getSelection = function () {
        return this.core.document.defaultView.getSelection();
    };
    /**
     * Save the current selection in editor so that when focus again, the selection can be restored
     */
    Editor.prototype.saveSelectionRange = function () {
        this.core.cachedSelectionRange = this.core.api.getSelectionRange(this.core, false /*tryGetFromCache*/);
    };
    /**
     * Restore the saved selection range and clear it
     */
    Editor.prototype.restoreSavedRange = function () {
        this.select(this.core.cachedSelectionRange);
        this.core.cachedSelectionRange = null;
    };
    /**
     * Get current focused position. Return null if editor doesn't have focus at this time.
     */
    Editor.prototype.getFocusedPosition = function () {
        var sel = this.getSelection();
        if (this.contains(sel && sel.focusNode)) {
            return new roosterjs_editor_dom_1.Position(sel.focusNode, sel.focusOffset);
        }
        var range = this.getSelectionRange();
        if (range) {
            return roosterjs_editor_dom_1.Position.getStart(range);
        }
        return null;
    };
    /**
     * Get a rect representing the location of the cursor.
     * @returns a Rect object representing cursor location
     */
    Editor.prototype.getCursorRect = function () {
        var position = this.getFocusedPosition();
        return position && roosterjs_editor_dom_1.getPositionRect(position);
    };
    /**
     * Get an HTML element from current cursor position.
     * When expectedTags is not specified, return value is the current node (if it is HTML element)
     * or its parent node (if current node is a Text node).
     * When expectedTags is specified, return value is the first anscestor of current node which has
     * one of the expected tags.
     * If no element found within editor by the given tag, return null.
     * @param selector Optional, an HTML selector to find HTML element with.
     * @param startFrom Start search from this node. If not specified, start from current focused position
     */
    Editor.prototype.getElementAtCursor = function (selector, startFrom) {
        if (!startFrom) {
            var position = this.getFocusedPosition();
            startFrom = position && position.node;
        }
        return startFrom && roosterjs_editor_dom_1.findClosestElementAncestor(startFrom, this.core.contentDiv, selector);
    };
    /**
     * Check if this position is at beginning of the editor.
     * This will return true if all nodes between the beginning of target node and the position are empty.
     * @param position The position to check
     * @returns True if position is at beginning of the editor, otherwise false
     */
    Editor.prototype.isPositionAtBeginning = function (position) {
        return roosterjs_editor_dom_1.isPositionAtBeginningOf(position, this.core.contentDiv);
    };
    /**
     * Get impacted regions from selection
     */
    Editor.prototype.getSelectedRegions = function (type) {
        if (type === void 0) { type = 0 /* Table */; }
        // Make sure there is a wrpper around cursor first, otherwise there will be no valid region
        this.core.corePlugins.typeInContainer.ensureTypeInElement(this.getFocusedPosition() || new roosterjs_editor_dom_1.Position(this.core.contentDiv, 0 /* Begin */));
        var range = this.getSelectionRange();
        return range ? roosterjs_editor_dom_1.getRegionsFromRange(this.core.contentDiv, range, type) : [];
    };
    Editor.prototype.addDomEventHandler = function (nameOrMap, handler) {
        var _this = this;
        if (nameOrMap instanceof Object) {
            var handlers_1 = Object.keys(nameOrMap)
                .map(function (eventName) {
                return nameOrMap[eventName] &&
                    _this.core.api.attachDomEvent(_this.core, eventName, null /*pluginEventType*/, nameOrMap[eventName]);
            })
                .filter(function (x) { return x; });
            return function () { return handlers_1.forEach(function (handler) { return handler(); }); };
        }
        else {
            return this.core.api.attachDomEvent(this.core, nameOrMap, null /*pluginEventType*/, handler);
        }
    };
    /**
     * Trigger an event to be dispatched to all plugins
     * @param eventType Type of the event
     * @param data data of the event with given type, this is the rest part of PluginEvent with the given type
     * @param broadcast indicates if the event needs to be dispatched to all plugins
     * True means to all, false means to allow exclusive handling from one plugin unless no one wants that
     * @returns the event object which is really passed into plugins. Some plugin may modify the event object so
     * the result of this function provides a chance to read the modified result
     */
    Editor.prototype.triggerPluginEvent = function (eventType, data, broadcast) {
        var event = __assign({ eventType: eventType }, data);
        this.core.api.triggerEvent(this.core, event, broadcast);
        return event;
    };
    /**
     * @deprecated Use triggerPluginEvent instead
     */
    Editor.prototype.triggerEvent = function (pluginEvent, broadcast) {
        if (broadcast === void 0) { broadcast = true; }
        this.core.api.triggerEvent(this.core, pluginEvent, broadcast);
    };
    /**
     * Trigger a ContentChangedEvent
     * @param source Source of this event, by default is 'SetContent'
     * @param data additional data for this event
     */
    Editor.prototype.triggerContentChangedEvent = function (source, data) {
        if (source === void 0) { source = "SetContent" /* SetContent */; }
        this.triggerPluginEvent(6 /* ContentChanged */, {
            source: source,
            data: data,
        });
    };
    //#endregion
    //#region Undo API
    /**
     * Undo last edit operation
     */
    Editor.prototype.undo = function () {
        this.focus();
        this.core.corePlugins.undo.undo();
    };
    /**
     * Redo next edit operation
     */
    Editor.prototype.redo = function () {
        this.focus();
        this.core.corePlugins.undo.redo();
    };
    /**
     * Add undo snapshot, and execute a format callback function, then add another undo snapshot, then trigger
     * ContentChangedEvent with given change source.
     * If this function is called nested, undo snapshot will only be added in the outside one
     * @param callback The callback function to perform formatting, returns a data object which will be used as
     * the data field in ContentChangedEvent if changeSource is not null.
     * @param changeSource The change source to use when fire ContentChangedEvent. When the value is not null,
     * a ContentChangedEvent will be fired with change source equal to this value
     */
    Editor.prototype.addUndoSnapshot = function (callback, changeSource) {
        this.core.api.editWithUndo(this.core, callback, changeSource);
    };
    /**
     * Perform an auto complete action in the callback, save a snapsnot of content before the action,
     * and trigger ContentChangedEvent with the change source if specified
     * @param callback The auto complete callback, return value will be used as data field of ContentChangedEvent
     * @param changeSource Chagne source of ContentChangedEvent. If not passed, no ContentChangedEvent will be  triggered
     */
    Editor.prototype.performAutoComplete = function (callback, changeSource) {
        this.core.corePlugins.edit.performAutoComplete(callback, changeSource);
    };
    /**
     * Whether there is an available undo snapshot
     */
    Editor.prototype.canUndo = function () {
        return this.core.corePlugins.undo.canUndo();
    };
    /**
     * Whether there is an available redo snapshot
     */
    Editor.prototype.canRedo = function () {
        return this.core.corePlugins.undo.canRedo();
    };
    //#endregion
    //#region Misc
    /**
     * Get document which contains this editor
     * @returns The HTML document which contains this editor
     */
    Editor.prototype.getDocument = function () {
        return this.core.document;
    };
    /**
     * Get the scroll container of the editor
     */
    Editor.prototype.getScrollContainer = function () {
        return this.core.scrollContainer;
    };
    /**
     * Get custom data related to this editor
     * @param key Key of the custom data
     * @param getter Getter function. If custom data for the given key doesn't exist,
     * call this function to get one and store it if it is specified. Otherwise return undefined
     * @param disposer An optional disposer function to dispose this custom data when
     * dispose editor.
     */
    Editor.prototype.getCustomData = function (key, getter, disposer) {
        return this.core.api.getCustomData(this.core, key, getter, disposer);
    };
    /**
     * Check if editor is in IME input sequence
     * @returns True if editor is in IME input sequence, otherwise false
     */
    Editor.prototype.isInIME = function () {
        return this.core.corePlugins.domEvent.isInIME();
    };
    /**
     * Get default format of this editor
     * @returns Default format object of this editor
     */
    Editor.prototype.getDefaultFormat = function () {
        return this.core.defaultFormat;
    };
    /**
     * Get a content traverser for the whole editor
     * @param startNode The node to start from. If not passed, it will start from the beginning of the body
     */
    Editor.prototype.getBodyTraverser = function (startNode) {
        return roosterjs_editor_dom_1.ContentTraverser.createBodyTraverser(this.core.contentDiv, startNode);
    };
    /**
     * Get a content traverser for current selection
     */
    Editor.prototype.getSelectionTraverser = function () {
        var range = this.getSelectionRange();
        return (range &&
            roosterjs_editor_dom_1.ContentTraverser.createSelectionTraverser(this.core.contentDiv, this.getSelectionRange()));
    };
    /**
     * Get a content traverser for current block element start from specified position
     * @param startFrom Start position of the traverser. Default value is ContentPosition.SelectionStart
     */
    Editor.prototype.getBlockTraverser = function (startFrom) {
        if (startFrom === void 0) { startFrom = 3 /* SelectionStart */; }
        var range = this.getSelectionRange();
        return (range && roosterjs_editor_dom_1.ContentTraverser.createBlockTraverser(this.core.contentDiv, range, startFrom));
    };
    /**
     * Get a text traverser of current selection
     */
    Editor.prototype.getContentSearcherOfCursor = function () {
        var range = this.getSelectionRange();
        return range && new roosterjs_editor_dom_1.PositionContentSearcher(this.core.contentDiv, roosterjs_editor_dom_1.Position.getStart(range));
    };
    /**
     * Run a callback function asynchronously
     * @param callback The callback function to run
     */
    Editor.prototype.runAsync = function (callback) {
        var _this = this;
        var win = this.core.contentDiv.ownerDocument.defaultView || window;
        win.requestAnimationFrame(function () {
            if (!_this.isDisposed() && callback) {
                callback();
            }
        });
    };
    /**
     * Set DOM attribute of editor content DIV
     * @param name Name of the attribute
     * @param value Value of the attribute
     */
    Editor.prototype.setEditorDomAttribute = function (name, value) {
        if (value === null) {
            this.core.contentDiv.removeAttribute(name);
        }
        else {
            this.core.contentDiv.setAttribute(name, value);
        }
    };
    /**
     * get DOM attribute of editor content DIV
     * @param name Name of the attribute
     */
    Editor.prototype.getEditorDomAttribute = function (name) {
        return this.core.contentDiv.getAttribute(name);
    };
    /**
     * Add a Content Edit feature. This is mostly called from ContentEdit plugin
     * @param feature The feature to add
     */
    Editor.prototype.addContentEditFeature = function (feature) {
        this.core.corePlugins.edit.addFeature(feature);
    };
    /**
     * Get style based format state from current selection, including font name/size and colors
     */
    Editor.prototype.getStyleBasedFormatState = function (node) {
        if (!node) {
            var range = this.getSelectionRange();
            node = range && roosterjs_editor_dom_1.Position.getStart(range).normalize().node;
        }
        return this.core.api.getStyleBasedFormatState(this.core, node);
    };
    //#endregion
    //#region Dark mode APIs
    /**
     * Set the dark mode state and transforms the content to match the new state.
     * @param nextDarkMode The next status of dark mode. True if the editor should be in dark mode, false if not.
     */
    Editor.prototype.setDarkModeState = function (nextDarkMode) {
        if (this.isDarkMode() == nextDarkMode) {
            return;
        }
        var currentContent = this.getContent(undefined /* triggerContentChangedEvent */, true /* getSelectionMarker */);
        this.core.inDarkMode = nextDarkMode;
        this.core.defaultFormat = calculateDefaultFormat_1.calculateDefaultFormat(this.core.contentDiv, this.core.defaultFormat, this.core.inDarkMode);
        this.setContent(currentContent);
        this.triggerPluginEvent(14 /* DarkModeChanged */, {
            changedToDarkMode: nextDarkMode,
        });
    };
    /**
     * Check if the editor is in dark mode
     * @returns True if the editor is in dark mode, otherwise false
     */
    Editor.prototype.isDarkMode = function () {
        return this.core.inDarkMode;
    };
    /**
     * Returns the dark mode options set on the editor
     * @returns A DarkModeOptions object
     */
    Editor.prototype.getDarkModeOptions = function () {
        return this.core.darkModeOptions;
    };
    /**
     * Whether experiment features can be used
     */
    Editor.prototype.useExperimentFeatures = function () {
        return !!this.enableExperimentFeatures;
    };
    return Editor;
}());
exports.default = Editor;
//# sourceMappingURL=Editor.js.map